import { useCallback, useState, useMemo, useEffect } from "react"

import { useMatch } from "react-router-dom"

import { useGeoServerFeaturesList, useGeoServerRetrieveFeature } from "@l2r-front/l2r-geodata"
import { useMapContext } from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { BottomSheetHeights, useApplicationDispatchContext, useCrossTabState } from "@l2r-front/l2r-utils"

import { IMAGE_COLLECTION_TYPE_PHOTO, IMAGE_COLLECTION_TYPE_STREETVIEW, IMAGE_COLLECTION_TYPES } from "../../constants/imageCollectionsTypes"
import { imageCollectionQueryKeys } from "../../hooks/queries/imageCollection/queryKeys"
import { useLatestImageCollection } from "../../hooks/queries/imageCollection/useLatestImageCollection"

import { ImageCollectionDispatchContext, ImageCollectionStateContext, initialState } from "./ImageCollectionContext.context"

export const ImageCollectionContextProvider = (props) => {
    const { children } = props
    const { selectedNetwork } = useNetworksStateContext()
    const isMobile = useIsMobileDevice()
    const matchDetailView = useMatch(":slug/:appId/:moduleId/:road/:detailId/*")
    const matchRoadView = useMatch(":slug/:appId/:moduleId/:road")

    const { data: currentRoad } = useGeoServerFeaturesList(imageCollectionQueryKeys["layer"](decodeURIComponent(matchRoadView?.params.road)), {
        layer: selectedNetwork.referential.layerSection,
        linear_location_road: decodeURIComponent(matchRoadView?.params.road),
    }, {
        enabled: !!matchRoadView?.params.road && !matchDetailView?.params.detailId,
    })

    const [mapState, mapDispatch] = useMapContext()
    const { setImageLayerDisplayed, setDesktopOpenPhoto, setMapHeightPct, setSelectedImageCollectionType } = mapDispatch
    const { desktopOpenPhoto, imageLayerDisplayed, selectedImageCollectionType } = mapState
    const { setCurrentBottomSheetHeight } = useApplicationDispatchContext()

    const [selectedImageCollectionRoad, setSelectedImageCollectionRoad] = useCrossTabState("selectedImageCollectionRoad", null)
    const [selectedImageCurrentYear, setImageCollectionCurrentYear] = useCrossTabState("selectedImageCurrentYear", null)
    const [triggeringFeatureId, setTriggeringFeatureId] = useCrossTabState("triggeringFeatureId", null)
    const [displayedImagePosition, setDisplayedImagePosition] = useCrossTabState("displayedImagePosition", null)
    const [imageCollectionState, setImageCollectionState] = useState(initialState)
    const [imageFeatureClickedPosition, setImageFeatureClickedPosition] = useCrossTabState("imageFeatureClickedPosition", null)

    const {
        data: imageCollection,
        isLoading: isLoadingImageCollections,
    } = useLatestImageCollection(selectedNetwork?.slug, selectedImageCurrentYear)

    const {
        data: triggeringFeature,
        isLoading: isLoadingTriggeringFeature,
    } = useGeoServerRetrieveFeature(triggeringFeatureId, null, { enabled: !!triggeringFeatureId })

    const setImageIdClicked = useCallback((imageId) => {
        setImageCollectionState(value => ({
            ...value,
            imageIdClicked: imageId,
        }))
    }, [setImageCollectionState])

    const setSegmentIdClicked = useCallback((segmentId) => {
        setImageCollectionState(value => ({
            ...value,
            segmentIdClicked: segmentId,
        }))
    }, [setImageCollectionState])

    const setSegmentFeatureIdClicked = useCallback((segmentFeatureId) => {
        setImageCollectionState(value => ({
            ...value,
            segmentIdClicked: segmentFeatureId,
            imageIdClicked: null,
        }))
        setSegmentIdClicked(segmentFeatureId)
    }, [setImageCollectionState, setSegmentIdClicked])

    const isImageCollectionAvailable = useCallback(() => {
        return !!imageCollection && (IMAGE_COLLECTION_TYPES.includes(imageCollection.type) || !isMobile)
    }, [imageCollection, isMobile])

    useEffect(() => {
        if (!isLoadingImageCollections && selectedImageCollectionType === IMAGE_COLLECTION_TYPE_PHOTO) {
            setSelectedImageCollectionType(imageCollection ? imageCollection.type : IMAGE_COLLECTION_TYPE_STREETVIEW)
        }
    }, [imageCollection, isLoadingImageCollections, selectedImageCollectionType, setSelectedImageCollectionType])

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            if (imageCollectionState.imageIdClicked) {
                setTriggeringFeatureId(imageCollectionState.imageIdClicked)
            } else if (imageCollectionState.segmentIdClicked) {
                setTriggeringFeatureId(imageCollectionState.segmentIdClicked)
            }
            return cancelAnimationFrame(frame)
        })
    }, [imageCollectionState.imageIdClicked, isMobile, setImageIdClicked, imageCollectionState.segmentIdClicked, setSegmentIdClicked, setTriggeringFeatureId])

    const resetImageCollectionType = useCallback(() => {
        const imageCollectionType = imageCollection?.type ? imageCollection?.type : IMAGE_COLLECTION_TYPE_STREETVIEW
        setSelectedImageCollectionType(imageCollectionType)
    }, [setSelectedImageCollectionType, imageCollection?.type])

    const resetImageCollectionDisplay = useCallback(() => {
        setImageLayerDisplayed(false)
        resetImageCollectionType()
        setDisplayedImagePosition(null)
    }, [setImageLayerDisplayed, setDisplayedImagePosition, resetImageCollectionType])

    const openImageViewer = useCallback(() => {
        setMapHeightPct(50)
        setCurrentBottomSheetHeight(BottomSheetHeights.CLOSED)
    }, [setCurrentBottomSheetHeight, setMapHeightPct])

    useEffect(() => {
        const handleChildWindowClosed = (event) => {
            if (event.data === "childWindowClosed") {
                resetImageCollectionDisplay()
            }
        }

        window.addEventListener("message", handleChildWindowClosed)

        return () => {
            window.removeEventListener("message", handleChildWindowClosed)
        }
    }, [resetImageCollectionDisplay])

    const onImageViewerButtonClicked = useCallback(() => {
        openImageViewer()
        setImageLayerDisplayed(true)
    }, [openImageViewer, setImageLayerDisplayed])

    const onSwitchImageLayer = useCallback((event) => {
        setImageLayerDisplayed(event.target.checked)
    }, [setImageLayerDisplayed])

    const setImageFeatureIdClicked = useCallback((imageFeatureId) => {
        setImageFeatureClickedPosition(null)
        setImageIdClicked(imageFeatureId)
        if (imageFeatureId) {
            openImageViewer()
        }
    }, [setImageFeatureClickedPosition, setImageIdClicked, openImageViewer])

    useEffect(() => {
        const road = decodeURIComponent(matchRoadView?.params.road || matchDetailView?.params.road)
        if (road && road !== "undefined") {
            setSelectedImageCollectionRoad(road)
        }
        if (!road || road === "undefined") {
            resetImageCollectionDisplay()
        }
    }, [
        matchRoadView?.params.road,
        matchDetailView,
        setSelectedImageCollectionRoad,
        resetImageCollectionDisplay])

    useEffect(() => {
        if (desktopOpenPhoto) {
            setImageLayerDisplayed(true)
            openImageViewer()
            setDesktopOpenPhoto(false)
        }
    }, [desktopOpenPhoto, imageCollection, openImageViewer, setDesktopOpenPhoto, setImageLayerDisplayed, setSelectedImageCollectionType])

    useEffect(() => {
        if (!matchDetailView?.params?.detailId && matchRoadView) {
            setImageFeatureClickedPosition(null)

            const currentRoadId = currentRoad?.[0]?.uuid
            if (!!currentRoadId && triggeringFeature && triggeringFeatureId !== currentRoadId && !isLoadingTriggeringFeature) {
                const isSameRoad = triggeringFeature.linear_locations[0].road === currentRoad[0].linear_locations[0].road
                if (!isSameRoad) {
                    setTriggeringFeatureId(currentRoadId)
                    setSegmentFeatureIdClicked(currentRoadId)
                }
            }
        }
    }, [currentRoad,
        isLoadingTriggeringFeature,
        matchDetailView,
        matchRoadView,
        setSegmentFeatureIdClicked,
        setImageFeatureClickedPosition,
        setTriggeringFeatureId,
        triggeringFeature,
        triggeringFeatureId])

    const stateValue = useMemo(() => {
        return {
            displayedImagePosition: displayedImagePosition,
            triggeringFeatureId: triggeringFeatureId,
            selectedImageCollectionType: selectedImageCollectionType,
            selectedImageCollectionRoad: selectedImageCollectionRoad,
            imageLayerDisplayed,
            imageCollection: imageCollection,
            imageFeatureClickedPosition,
            isLoadingImageCollections,
        }
    }, [
        displayedImagePosition,
        imageLayerDisplayed,
        triggeringFeatureId,
        selectedImageCollectionType,
        selectedImageCollectionRoad,
        imageCollection,
        imageFeatureClickedPosition,
        isLoadingImageCollections,
    ])

    const dispatchValue = useMemo(() => {
        return {
            onOpenImageViewer: matchRoadView?.params.road ? onImageViewerButtonClicked : null,
            onSwitchImageLayer: matchRoadView?.params.road ? onSwitchImageLayer : null,
            setImageLayerDisplayed,
            setDisplayedImagePosition,
            resetImageCollectionType,
            resetImageCollectionDisplay,
            setTriggeringFeatureId,
            setSelectedImageCollectionType,
            setImageFeatureIdClicked,
            setImageFeatureClickedPosition,
            setSegmentFeatureIdClicked,
            isImageCollectionAvailable,
            setImageCollectionCurrentYear,
        }
    }, [
        matchRoadView?.params.road,
        onImageViewerButtonClicked,
        onSwitchImageLayer,
        setImageLayerDisplayed,
        setDisplayedImagePosition,
        resetImageCollectionType,
        resetImageCollectionDisplay,
        setSelectedImageCollectionType,
        setTriggeringFeatureId,
        setImageFeatureClickedPosition,
        setImageFeatureIdClicked,
        setSegmentFeatureIdClicked,
        isImageCollectionAvailable,
        setImageCollectionCurrentYear,
    ])

    return (
        <ImageCollectionStateContext.Provider value={stateValue}>
            <ImageCollectionDispatchContext.Provider value={dispatchValue}>
                {children}
            </ImageCollectionDispatchContext.Provider>
        </ImageCollectionStateContext.Provider>
    )
}

ImageCollectionContextProvider.propTypes = {
    children: PropTypes.node,
}